import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PERSON_DASHBOARD, TEST_HISTORY } from "../../../../routes/routerPath";
import { userInfoFetch } from "../../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../../utlis/locale";
import { rememberMobilityReportSnapshot } from "../../../../utlis/mobilityReportSnapshot";
import { useT } from "../../../../i18n/useT";
import {
  fetchMobilityReport,
  patchMobilityReport,
  createMobilityReportShare,
  resolveMobilityShareUrl,
} from "../../../../services/mobilityReportApi";
import RenderReportDocument from "./RenderReportDocument";
import {
  printReport,
  reportErrorKey,
  reportEditError,
} from "./reportPresentation";
import "./MobilityReportV2.scss";

const requestId = () =>
  window.crypto?.randomUUID?.() ||
  `report-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const MobilityReport = () => {
  const t = useT("mobilityRender");
  const tSts = useT("sts");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const seniorId =
    params.get("seniorId") || params.get("seniorUuid") || userInfoFetch()?.id;
  const reportId =
    params.get("reportId") || params.get("report_id") || undefined;
  const revision = params.get("revision") || undefined;
  const activityId = params.get("activity_id") || undefined;
  const locale = getCurrentLocale();
  const unsupportedSts =
    params.get("moduleId") === "10" &&
    !reportId &&
    params.get("reportSource") !== "v6";
  const routeKey = JSON.stringify([
    seniorId,
    reportId,
    revision,
    activityId,
    locale,
    unsupportedSts,
  ]);
  const currentKey = useRef(routeKey);
  currentKey.current = routeKey;
  const [reload, setReload] = useState(0);
  const [state, setState] = useState({
    status: "loading",
    document: null,
    etag: null,
    key: routeKey,
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const shareCache = useRef(null);
  const sharePromise = useRef(null);
  const writeKeys = useRef(new Map());
  const stableWriteKey = (identity) => {
    if (!writeKeys.current.has(identity))
      writeKeys.current.set(identity, requestId());
    return writeKeys.current.get(identity);
  };
  const mutation = useRef(0);
  const root = useRef(null);
  const dialogRef = useRef(null);
  const shareTrigger = useRef(null);
  const openShare = () => {
    shareTrigger.current = document.activeElement;
    setShareOpen(true);
  };
  const actionLock = useRef(false);
  const [safeShareUrl, setSafeShareUrl] = useState("");
  useEffect(() => {
    if (!shareOpen) return undefined;
    const previous = shareTrigger.current || document.activeElement;
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShareOpen(false);
      }
      if (event.key === "Tab") {
        const controls = Array.from(
          dialogRef.current?.querySelectorAll("button:not(:disabled), input") ||
            [],
        );
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      previous?.focus?.();
    };
  }, [shareOpen]);
  useEffect(() => {
    const controller = new AbortController();
    const key = routeKey;
    mutation.current += 1;
    setBusy(false);
    setNotice("");
    setShareOpen(false);
    setPreview(false);
    shareCache.current = null;
    sharePromise.current = null;
    setSafeShareUrl("");
    actionLock.current = false;
    setState({ status: "loading", document: null, etag: null, key });
    if (unsupportedSts || !seniorId || (reportId && !revision)) {
      setState({
        status: unsupportedSts
          ? "sts"
          : !seniorId
            ? "missingPerson"
            : "unsupported",
        document: null,
        key,
      });
      return () => controller.abort();
    }
    fetchMobilityReport(seniorId, {
      reportId,
      revision,
      activityId,
      locale,
      signal: controller.signal,
    })
      .then((result) => {
        if (!controller.signal.aborted && currentKey.current === key)
          setState({ ...result, status: "ok", key });
      })
      .catch((error) => {
        if (!controller.signal.aborted && currentKey.current === key)
          setState({ status: reportErrorKey(error), document: null, key });
      });
    return () => controller.abort();
  }, [
    routeKey,
    reload,
    seniorId,
    reportId,
    revision,
    activityId,
    locale,
    unsupportedSts,
  ]);
  const visible = state.key === routeKey ? state : { status: "loading" };
  const report = visible.document;
  const can = (name) =>
    report?.capabilities?.[name] === true ||
    report?.capabilities?.[name]?.allowed === true;
  const back = () =>
    window.history.length > 1
      ? navigate(-1)
      : navigate(
          `${PERSON_DASHBOARD}?seniorId=${encodeURIComponent(seniorId || "")}&tab=metrics`,
        );
  const history = () =>
    navigate(`${TEST_HISTORY}?seniorUuid=${encodeURIComponent(seniorId)}`);
  const edit = async (change) => {
    if (busy || actionLock.current || !report) return;
    actionLock.current = true;
    const key = routeKey;
    const operation = ++mutation.current;
    setBusy(true);
    setNotice("");
    const writeIdentity = JSON.stringify([
      seniorId,
      report.report_id,
      report.revision,
      change,
    ]);
    try {
      const result = await patchMobilityReport(
        seniorId,
        report,
        state.etag,
        change,
        { idempotencyKey: stableWriteKey(writeIdentity) },
      );
      if (currentKey.current !== key || mutation.current !== operation) return;
      writeKeys.current.delete(writeIdentity);
      setState({ ...result, status: "ok", key });
      shareCache.current = null;
      sharePromise.current = null;
      setSafeShareUrl("");
      setNotice(t("saveSuccess"));
    } catch (error) {
      if (currentKey.current !== key || mutation.current !== operation) return;
      if ((error?.response?.status || error?.status) === 412) {
        setNotice(t("edit_conflict"));
        try {
          // Exact revision links need the current revision of their own lineage.
          const result = await fetchMobilityReport(seniorId, {
            activityId,
            locale,
          });
          if (result.document.report_id !== report.report_id)
            throw new Error("Different report lineage");
          if (currentKey.current === key && mutation.current === operation) {
            setState({ ...result, status: "ok", key });
            shareCache.current = null;
            sharePromise.current = null;
          }
        } catch {
          if (currentKey.current === key) setNotice(t("conflictRefreshFailed"));
        }
      } else setNotice(reportEditError(error, t));
      // The renderer preserves its draft until success; never silently resubmit.
      throw error;
    } finally {
      if (currentKey.current === key && mutation.current === operation) {
        setBusy(false);
        actionLock.current = false;
      }
    }
  };
  const shareUrl = async () => {
    const identity = `${report.report_id}:${report.revision}`;
    if (shareCache.current?.identity === identity)
      return shareCache.current.url;
    if (sharePromise.current?.identity === identity)
      return sharePromise.current.promise;
    const key = routeKey;
    const promise = createMobilityReportShare(seniorId, report, {
      idempotencyKey: stableWriteKey(
        JSON.stringify(["share", seniorId, identity]),
      ),
    })
      .then((share) => {
        const url = resolveMobilityShareUrl(share, window.location.origin);
        if (!url) throw new Error("Invalid share URL");
        if (currentKey.current !== key || mutation.current !== operation)
          throw new Error("Report changed");
        shareCache.current = { identity, url };
        rememberMobilityReportSnapshot(seniorId, share.slug);
        return url;
      })
      .finally(() => {
        if (sharePromise.current?.identity === identity)
          sharePromise.current = null;
      });
    const operation = mutation.current;
    sharePromise.current = { identity, promise };
    return promise;
  };
  const shareAction = async (kind) => {
    if (actionLock.current) return;
    actionLock.current = true;
    const key = routeKey;
    const operation = mutation.current;
    const isCurrent = () =>
      currentKey.current === key && mutation.current === operation;
    setBusy(true);
    setNotice("");
    try {
      const url = await shareUrl();
      if (!isCurrent()) return;
      setSafeShareUrl(url);
      if (kind === "copy") {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          if (isCurrent()) setNotice(t("copied"));
        }
      } else if (kind === "email")
        window.location.href = `mailto:?subject=${encodeURIComponent(report.presentation?.title || t("report"))}&body=${encodeURIComponent(url)}`;
      else if (navigator.share) {
        await navigator.share({
          title: report.presentation?.title || t("report"),
          url,
        });
        if (isCurrent()) setNotice(t("shareSuccess"));
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        if (isCurrent()) setNotice(t("copied"));
      }
    } catch (error) {
      if (isCurrent() && error?.name !== "AbortError")
        setNotice(
          t(
            error?.code === "MobilityReportProcessing"
              ? "processing"
              : "share_error",
          ),
        );
    } finally {
      if (currentKey.current === key) {
        setBusy(false);
        actionLock.current = false;
      }
    }
  };
  const print = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    const key = routeKey;
    setBusy(true);
    try {
      await printReport(root.current, () => currentKey.current === key);
    } catch {
      if (currentKey.current === key) setNotice(t("print_error"));
    } finally {
      if (currentKey.current === key) {
        setBusy(false);
        actionLock.current = false;
      }
    }
  };
  const activityChange = (value) => {
    const next = new URLSearchParams(params);
    next.set("activity_id", value);
    next.delete("reportId");
    next.delete("report_id");
    next.delete("revision");
    next.set("reportSource", "v6");
    navigate({ search: next.toString() });
  };
  return (
    <main className="mobility-report-route mobility-render-route" ref={root}>
      <nav className="mobility-render-toolbar" aria-label={t("report")}>
        <button type="button" onClick={back}>
          {t("back")}
        </button>
        {report && (
          <>
            <strong>{report.presentation?.title || t("report")}</strong>
            {can("can_view_history") && (
              <button type="button" onClick={history}>
                {t("history")}
              </button>
            )}
            {can("can_print") && (
              <button
                type="button"
                onClick={() => setPreview((value) => !value)}
              >
                {t(preview ? "close" : "preview")}
              </button>
            )}
            {can("can_print") && (
              <button type="button" disabled={busy} onClick={print}>
                {t("print")}
              </button>
            )}
            {can("can_share") && (
              <button type="button" disabled={busy} onClick={openShare}>
                {t("share")}
              </button>
            )}
          </>
        )}
      </nav>
      {notice && (
        <p className="mobility-render-notice" role="status">
          {notice}
        </p>
      )}
      {!report ? (
        <section
          className="mobility-report-state"
          role={visible.status === "loading" ? "status" : "alert"}
        >
          <p>
            {visible.status === "sts"
              ? tSts("report_unavailable")
              : t(visible.status)}
          </p>
          {!["loading", "sts", "missingPerson"].includes(visible.status) && (
            <button
              type="button"
              onClick={() => setReload((value) => value + 1)}
            >
              {t("retry")}
            </button>
          )}
        </section>
      ) : (
        <RenderReportDocument
          report={report}
          readOnly={preview}
          onEdit={edit}
          onActivityChange={activityChange}
          onHistory={history}
          onBack={back}
          onShare={openShare}
          busy={busy}
        />
      )}
      {shareOpen && report && (
        <div
          className="mobility-render-modal-backdrop"
          onClick={() => setShareOpen(false)}
        >
          <section
            ref={dialogRef}
            className="mobility-render-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t("share")}
            onClick={(event) => event.stopPropagation()}
          >
            <h2>{t("share")}</h2>
            {safeShareUrl && (
              <input
                aria-label={t("copy_link")}
                readOnly
                value={safeShareUrl}
                onFocus={(event) => event.target.select()}
              />
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => shareAction("copy")}
            >
              {t("copy_link")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => shareAction("native")}
            >
              {t("share")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => shareAction("email")}
            >
              {t("email")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShareOpen(false);
                setPreview(true);
              }}
            >
              {t("preview")}
            </button>
            <button type="button" autoFocus onClick={() => setShareOpen(false)}>
              {t("close")}
            </button>
          </section>
        </div>
      )}
    </main>
  );
};
export default MobilityReport;
